(() => {
  if (window.sylaviePriorityServiceControllerLoaded) return;

  window.sylaviePriorityServiceControllerLoaded = true;

  const SECTION_SELECTOR = '[data-priority-service]';
  const REVEAL_SELECTOR = '[data-priority-service-reveal]';
  const STEP_SELECTOR = '[data-priority-service-step]';
  const PANEL_SELECTOR = '[data-priority-service-panel]';
  const VISIBLE_CLASS = 'is-visible';
  const INITIALIZED_CLASS = 'is-initialized';
  const STEP_STAGGER = 90;
  const PANEL_STAGGER = 110;
  const instances = new WeakMap();

  const formatStepNumber = (step, index) => {
    const number = step.querySelector('.priority-service__step-number');
    if (!number) return;

    number.textContent = String(index + 1).padStart(2, '0');
  };

  class PriorityServiceSection {
    constructor(section) {
      this.section = section;
      this.items = Array.from(section.querySelectorAll(REVEAL_SELECTOR));
      this.steps = Array.from(section.querySelectorAll(STEP_SELECTOR));
      this.panels = Array.from(section.querySelectorAll(PANEL_SELECTOR));
      this.observer = null;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotionChange = () => this.refresh();

      this.reduceMotion.addEventListener('change', this.onMotionChange);
      this.setStagger();
      this.refresh();
    }

    setStagger() {
      this.steps.forEach((step, index) => {
        formatStepNumber(step, index);
        step.style.setProperty('--priority-service-step-delay', `${index * STEP_STAGGER}ms`);
      });

      this.panels.forEach((panel, index) => {
        panel.style.setProperty('--priority-service-panel-delay', `${index * PANEL_STAGGER}ms`);
      });
    }

    refresh() {
      this.disconnect();

      if (this.reduceMotion.matches || !('IntersectionObserver' in window)) {
        this.section.classList.remove(INITIALIZED_CLASS);
        this.items.forEach((item) => item.classList.add(VISIBLE_CLASS));
        return;
      }

      this.section.classList.add(INITIALIZED_CLASS);
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add(VISIBLE_CLASS);
            this.observer.unobserve(entry.target);
          });
        },
        {
          rootMargin: '0px 0px -6% 0px',
          threshold: 0.08,
        }
      );

      this.items.forEach((item) => this.observer.observe(item));
    }

    disconnect() {
      if (!this.observer) return;

      this.observer.disconnect();
      this.observer = null;
    }

    destroy() {
      this.disconnect();
      this.reduceMotion.removeEventListener('change', this.onMotionChange);
      this.section.classList.remove(INITIALIZED_CLASS);
    }
  }

  const getSections = (root = document) => {
    if (root.matches?.(SECTION_SELECTOR)) return [root];

    return Array.from(root.querySelectorAll(SECTION_SELECTOR));
  };

  const initSections = (root = document) => {
    getSections(root).forEach((section) => {
      const existing = instances.get(section);
      if (existing) existing.destroy();

      instances.set(section, new PriorityServiceSection(section));
    });
  };

  const destroySections = (root) => {
    getSections(root).forEach((section) => {
      const instance = instances.get(section);
      if (!instance) return;

      instance.destroy();
      instances.delete(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSections());
  } else {
    initSections();
  }

  document.addEventListener('shopify:section:load', (event) => initSections(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroySections(event.target));
})();
